import { createContext, useContext, useId, type ReactNode } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

/*
 * Title → content gap of the Figma cards. The designs mix 24 px (Registrujem sa ako, Prihlasovacie
 * údaje, Osobné údaje, Údaje o firme) and 16 px (the three address / contact cards); on phones it is
 * 16 px everywhere except „Prihlasovacie údaje“. Kept as designed, see the report.
 */
const SPACING = {
  default: 'gap-4 md:gap-6',
  spacious: 'gap-6',
  compact: 'gap-4',
} as const;

export type RegisterLayout = 'page' | 'dialog';

const RegisterLayoutContext = createContext<RegisterLayout>('page');

export const RegisterLayoutProvider = RegisterLayoutContext.Provider;

export const useRegisterLayout = () => useContext(RegisterLayoutContext);

/**
 * Section of the registration. On the page it is the Figma „Doprava“ card: 1 px border/subtle, radius
 * 18, 24 px padding (16 on phones). In the dialog the dialog card is the surface, so the section is
 * flat: the title over its fields and a 1 px line with 24 px above every section but the first.
 */
export function RegisterCard({
  title,
  hint,
  spacing = 'default',
  first = false,
  children,
}: {
  title: ReactNode;
  /** Helper under the title (Figma „Nepovinné údaje …“). */
  hint?: ReactNode;
  spacing?: keyof typeof SPACING;
  /** In the dialog the first section has no line above it. */
  first?: boolean;
  children: ReactNode;
}) {
  const titleId = useId();
  const dialog = useRegisterLayout() === 'dialog';
  const Heading = dialog ? 'h3' : 'h2';
  const headingClass = dialog ? 'dlz-h4 text-[#0F1012]' : 'dlz-h3 text-[#0F1012]';

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        'flex flex-col',
        dialog
          ? !first && 'border-t border-[#E2E4E7] pt-6'
          : 'rounded-[18px] border border-[#E2E4E7] bg-white p-4 md:p-6',
        SPACING[spacing],
      )}
    >
      {hint ? (
        <header className="flex flex-col gap-2 md:gap-3">
          <Heading id={titleId} className={headingClass}>
            {title}
          </Heading>
          <p className="dlz-body-s md:dlz-caption text-[#6B7079]">{hint}</p>
        </header>
      ) : (
        <Heading id={titleId} className={headingClass}>
          {title}
        </Heading>
      )}
      {children}
    </section>
  );
}
